import { type ReactNode } from 'react';

import { Spinner, useMediaQuery } from '@chakra-ui/react';
import { LuX } from 'react-icons/lu';

import { TailwindBreakpoints } from '../../enums/screenBreakpoints';
import { useSidebarStore } from '../../store/Sidebar/useSidebarStore';

interface AlertCardProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  onDismiss?: () => void;
  dismissLabel?: string;
  actionLoading?: boolean;
  dataTestId?: string;
}

const stylesheet = {
  container:
    'fixed z-[1400] left-3 right-3 sm:top-4 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:w-[calc(100%-2rem)] max-w-2xl flex flex-row items-center justify-between gap-3 p-3 rounded-semantic border border-emerald-700/20 bg-semantic-bg-surface shadow-lg',
  content: 'flex flex-row items-center gap-3 flex-1 min-w-0',
  icon: 'text-semantic-text-accent flex-shrink-0',
  textContainer: 'flex flex-col min-w-0',
  title: 'text-sm font-semibold text-semantic-text-primary',
  description: 'text-xs text-semantic-text-secondary truncate',
  actions: 'flex flex-row items-center gap-2 flex-shrink-0',
  actionButton:
    'inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-semantic-bg-accent hover:bg-semantic-bg-accent-hover rounded-semantic whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed',
  secondaryActionButton:
    'px-3 py-1.5 text-sm font-medium text-semantic-text-secondary border border-semantic-border-input hover:bg-semantic-bg-hover hover:text-semantic-text-primary rounded-semantic whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed',
};

export function AlertCard({
  icon,
  title,
  description,
  actionLabel,
  onAction,
  secondaryActionLabel,
  onSecondaryAction,
  onDismiss,
  dismissLabel,
  actionLoading = false,
  dataTestId = 'alert-card',
}: Readonly<AlertCardProps>) {
  const [isMobile] = useMediaQuery(`(max-width: ${TailwindBreakpoints.SM})`);
  const mobileAlertTopOffset = useSidebarStore((s) => s.mobileAlertTopOffset);

  const hasAction = actionLabel !== undefined && onAction !== undefined;
  const hasSecondaryAction =
    secondaryActionLabel !== undefined && onSecondaryAction !== undefined;
  const hasDismiss = onDismiss !== undefined;

  return (
    <div
      className={stylesheet.container}
      data-testid={dataTestId}
      role="alert"
      style={isMobile ? { top: `${mobileAlertTopOffset + 8}px` } : undefined}
    >
      <div className={stylesheet.content}>
        {icon !== undefined && <span className={stylesheet.icon}>{icon}</span>}
        <div className={stylesheet.textContainer}>
          <span className={stylesheet.title}>{title}</span>
          {description !== undefined && (
            <span className={stylesheet.description}>{description}</span>
          )}
        </div>
      </div>
      <div className={stylesheet.actions}>
        {hasSecondaryAction && (
          <button
            type="button"
            className={stylesheet.secondaryActionButton}
            onClick={onSecondaryAction}
            disabled={actionLoading}
          >
            {secondaryActionLabel}
          </button>
        )}
        {hasAction && (
          <button
            type="button"
            className={stylesheet.actionButton}
            onClick={onAction}
            disabled={actionLoading}
          >
            {actionLoading && <Spinner size="sm" color="white" />}
            {actionLabel}
          </button>
        )}
        {hasDismiss && (
          <button type="button" onClick={onDismiss} aria-label={dismissLabel}>
            <LuX />
          </button>
        )}
      </div>
    </div>
  );
}
